import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/hooks/use-toast';

export interface BackgroundJob {
  job_id: string;
  celery_task_id: string | null;
  tenant_id: string;
  job_type: string;
  entity_type: string;
  entity_id: string;
  document_metadata_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'retry';
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  retry_count: number;
  max_retries: number;
  processing_data: Record<string, unknown>;
}

interface UseBackgroundJobStatusOptions {
  jobId?: string;
  documentMetadataId?: string;
  entityId?: string;
  entityType?: string;
  onStatusChange?: (job: BackgroundJob) => void;
  onComplete?: (job: BackgroundJob) => void;
  onFailed?: (job: BackgroundJob) => void;
}

export function useBackgroundJobStatus({
  jobId,
  documentMetadataId,
  entityId,
  entityType,
  onStatusChange,
  onComplete,
  onFailed,
}: UseBackgroundJobStatusOptions) {
  const [jobs, setJobs] = useState<BackgroundJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Find active processing jobs
  const activeJobs = jobs.filter(job => ['pending', 'processing', 'retry'].includes(job.status));
  const completedJobs = jobs.filter(job => job.status === 'completed');
  const failedJobs = jobs.filter(job => job.status === 'failed');

  // Get the overall status
  const overallStatus = activeJobs.length > 0 
    ? (activeJobs.some(job => job.status === 'processing') ? 'processing' : 'pending')
    : (failedJobs.length > 0 && completedJobs.length === 0 ? 'failed' : 'completed');

  // Fetch job status from API
  const fetchJobStatus = useCallback(async () => {
    if (!jobId && !documentMetadataId) return;

    setIsLoading(true);
    setError(null);

    try {
      let url: string;
      if (jobId) {
        url = `/api/jobs/${jobId}/status`;
      } else if (documentMetadataId) {
        url = `/api/documents/${documentMetadataId}/jobs`;
      } else {
        return;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch job status: ${response.statusText}`);
      }

      const data = await response.json();
      const jobsArray = Array.isArray(data) ? data : [data];
      
      setJobs(jobsArray);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch job status';
      setError(errorMsg);
      console.error('Error fetching job status:', err);
    } finally {
      setIsLoading(false);
    }
  }, [jobId, documentMetadataId]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!jobId && !documentMetadataId && !entityId) return;

    // Initial fetch
    fetchJobStatus();

    // Set up real-time subscription
    const channel = supabase
      .channel('background_jobs_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'background_jobs',
          filter: jobId 
            ? `job_id=eq.${jobId}`
            : documentMetadataId 
            ? `document_metadata_id=eq.${documentMetadataId}`
            : `entity_id=eq.${entityId}`,
        },
        (payload) => {
          console.log('Background job status change:', payload);
          
          const updatedJob = payload.new as BackgroundJob;
          
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            setJobs(prevJobs => {
              const existingIndex = prevJobs.findIndex(job => job.job_id === updatedJob.job_id);
              if (existingIndex >= 0) {
                // Update existing job
                const newJobs = [...prevJobs];
                newJobs[existingIndex] = updatedJob;
                return newJobs;
              } else {
                // Add new job
                return [...prevJobs, updatedJob];
              }
            });

            // Call status change callback
            onStatusChange?.(updatedJob);

            // Handle completion
            if (updatedJob.status === 'completed') {
              onComplete?.(updatedJob);
              toast({
                title: "Processing Complete",
                description: `Document processing has completed successfully.`,
              });
            }

            // Handle failure
            if (updatedJob.status === 'failed') {
              onFailed?.(updatedJob);
              toast({
                title: "Processing Failed",
                description: updatedJob.error_message || "Document processing failed. Please try again.",
                variant: "destructive",
              });
            }
          }

          if (payload.eventType === 'DELETE') {
            setJobs(prevJobs => prevJobs.filter(job => job.job_id !== payload.old.job_id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [jobId, documentMetadataId, entityId, entityType, fetchJobStatus, onStatusChange, onComplete, onFailed, toast]);

  // Manual refresh function
  const refresh = useCallback(() => {
    fetchJobStatus();
  }, [fetchJobStatus]);

  return {
    jobs,
    activeJobs,
    completedJobs,
    failedJobs,
    overallStatus,
    isLoading,
    error,
    refresh,
  };
}